import {
  Button,
  Card,
  closeCurrentDialog,
  closeDialog,
  openDialog,
  TabProps,
  TabsHeader,
  Tooltip,
} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {GetWarehousesResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {refreshDmsDatagrid, usePermissions} from '@dms/shared';
import {ArticleCreate, getSingleOrUndefined} from '@dms/shared-aftersales';

import {composePath, suffixTestId, TestIdProps, useNavigate} from 'shared/web';

import {RequestTabMaterialPriceListTab} from './RequestTabMaterialPriceListTab';
import {RequestTabMaterialWarehouseTab} from './RequestTabMaterialWarehouseTab';

interface MaterialListProps extends TestIdProps {
  onWarehouseDispatch: (warehouseArticleId: string) => Promise<void>;
  onWarehouseRequest: (warehouseArticleId: string) => Promise<void>;
  onWarehouseNonBinding: (warehouseArticleId: string) => Promise<void>;
  availableWarehouses: GetWarehousesResponse;
  hasPendingRequest: boolean;
}

const TABS_IDS = {
  warehouseTab: 'directSale-addMaterial-warehouseTab',
  priceListTab: 'directSale-addMaterial-priceListTab',
};

export function RequestTabMaterialList(props: MaterialListProps) {
  const navigate = useNavigate();
  const [activeTabId, setActiveTabId] = useState(TABS_IDS.warehouseTab);
  const [dataGridFilters, setDataGridFilters] = useState<Record<string, string>>({});

  const [articleCreate] = usePermissions({
    permissionKeys: ['articleCreate'],
  });

  const tabs: TabProps[] = [
    {
      id: TABS_IDS.warehouseTab,
      title: i18n.t('entity.warehouse.labels.warehouse'),
      content: (
        <RequestTabMaterialWarehouseTab
          onWarehouseDispatch={props.onWarehouseDispatch}
          onWarehouseRequest={props.onWarehouseRequest}
          onWarehouseNonBinding={props.onWarehouseNonBinding}
          availableWarehouses={props.availableWarehouses}
          onDataGridFiltersChange={setDataGridFilters}
          hasPendingRequest={props.hasPendingRequest}
          data-testid={suffixTestId('tabs.warehouse', props)}
        />
      ),
    },
    {
      id: TABS_IDS.priceListTab,
      title: i18n.t('entity.warehouse.labels.priceList'),
      content: (
        <RequestTabMaterialPriceListTab
          onWarehouseRequest={props.onWarehouseRequest}
          onWarehouseNonBinding={props.onWarehouseNonBinding}
          onWarehouseDispatch={props.onWarehouseDispatch}
          hasPendingRequest={props.hasPendingRequest}
          data-testid={suffixTestId('tabs.priceList', props)}
        />
      ),
    },
  ];

  const activeTabContent = tabs.find(
    (tab) => tab.id === (activeTabId ?? TABS_IDS.warehouseTab)
  )?.content;

  const handleCreateArticle = () => {
    openDialog(
      <ArticleCreate
        defaultValues={{
          catalogNumber:
            'catalogNumber' in dataGridFilters ? dataGridFilters.catalogNumber : undefined,
          manufacturerId:
            'manufacturerId' in dataGridFilters
              ? getSingleOrUndefined(dataGridFilters.manufacturerId)
              : undefined,
          warehouseId:
            'warehouseId' in dataGridFilters
              ? getSingleOrUndefined(dataGridFilters.warehouseId)
              : undefined,
          name: 'name' in dataGridFilters ? dataGridFilters.name : undefined,
        }}
        onArticleCreate={(createdArticleData) => {
          if (createdArticleData.submittedButton === 'CREATE_AND_EDIT') {
            return navigate(
              composePath(warehouseRoutes.articleDetailOverview, {
                params: {
                  warehouseId: createdArticleData.formValues.warehouseId,
                  id: createdArticleData.articleId,
                },
                isSearchParamsPreserved: false,
              })
            );
          }
          closeCurrentDialog();
          refreshDmsDatagrid('spare-parts-ds');
        }}
        submitButtonTitle={i18n.t('general.actions.create')}
        onClose={() => closeDialog('createArticleDialog')}
        hasCreateAndEditButton
        data-testid={testIds.warehouse.priceList('dialogs.createArticle')}
      />,
      {
        id: 'createArticleDialog',
        title: i18n.t('entity.warehouse.labels.createNewArticle'),
        withAdditionalFooter: true,
        scrollBehavior: 'outside',
      }
    );
  };

  return (
    <VStack spacing={4} height="100%">
      <HStack justify="space-between">
        <TabsHeader
          variant="condensed"
          tabs={tabs}
          activeTabId={activeTabId}
          onChange={setActiveTabId}
          data-testid={suffixTestId('tabs', props)}
        />
        <Show when={activeTabId === TABS_IDS.warehouseTab}>
          <Tooltip
            isDisabled={articleCreate}
            label={i18n.t('module.warehouse.label.missingArticleCreatePermission')}
          >
            <Button
              variant="secondary"
              leftIcon="content/add_circle"
              onClick={handleCreateArticle}
              title={i18n.t('entity.warehouse.labels.createNewArticle')}
              isDisabled={!articleCreate}
              data-testid={suffixTestId('button.createNewArticle', props)}
            />
          </Tooltip>
        </Show>
      </HStack>
      <Card isFullHeight>
        <Box height="100%" flex={1}>
          {activeTabContent}
        </Box>
      </Card>
    </VStack>
  );
}
